import React from 'react';
import './Support.css';

const Support: React.FC = () => {
  return (
    <div className="support-page">
      <div className="container">
        <header className="page-header">
          <h1>Flow XP Support</h1>
          <p className="subtitle">How can we help you?</p>
        </header>

        <section className="contact-info">
          <div className="info-card">
            <h2>📧 Contact Us</h2>
            <p>For any questions or issues, please contact us at:</p>
            <a href="mailto:flowledgeteam@gmail.com" className="email-link">
              flowledgeteam@gmail.com
            </a>
            <p className="response-time">We typically respond within 24-48 hours</p>
          </div>
        </section>

        <section className="faq-section">
          <h2 className="section-title">Frequently Asked Questions</h2>

          <div className="faq-list">

            <div className="faq-item">
              <h3>🔐 How do I create an account?</h3>
              <p>
                Download Flow XP from the App Store (iOS) or Google Play Store (Android), open it,
                and tap "Sign Up". You'll need a valid email address, a password, and a username.
                A profile picture is optional.
              </p>
            </div>

            <div className="faq-item">
              <h3>🎯 What are the training classes?</h3>
              <p>
                Flow XP offers three specializations to choose from:
              </p>
              <ul>
                <li><strong>PowerFlow</strong> — Street Workout & Calisthenics</li>
                <li><strong>OverFlow</strong> — Freestyle Football</li>
                <li><strong>MasterFlow</strong> — Breaking</li>
              </ul>
              <p>
                You progress through levels within each class by submitting training videos
                that get validated by the community.
              </p>
            </div>

            <div className="faq-item">
              <h3>📹 How do training video submissions work?</h3>
              <p>
                Go to the Adventure section, choose your class, select a stage and level,
                then record or upload your training video. Your video will be reviewed by
                other users in the community. You need at least 2 approvals out of 3 evaluations
                for your video to be validated and your level to progress.
              </p>
            </div>

            <div className="faq-item">
              <h3>💰 How do I earn Flow points?</h3>
              <p>
                You earn Flow points by:
              </p>
              <ul>
                <li>Having your training video validated by the community (+25 Flow)</li>
                <li>Evaluating other users' training videos (+25 Flow)</li>
                <li>Watching ads in the "Earn Flow" section</li>
                <li>Opening chests earned through progression</li>
              </ul>
              <p>
                Flow points are used to craft items in the Forge and unlock content in the Shop.
              </p>
            </div>

            <div className="faq-item">
              <h3>🔥 What is the One Flow daily challenge?</h3>
              <p>
                The One Flow challenge is a daily exercise challenge available to all users.
                Completing it builds your streak. Your streak is tracked in the One Flow Ranking.
                Missing a day resets your streak — unless you use a Freeze token.
              </p>
            </div>

            <div className="faq-item">
              <h3>⭐ What is Premium and what do I get?</h3>
              <p>
                Flow XP Premium gives you:
              </p>
              <ul>
                <li>Ad-free experience</li>
                <li>Priority video validation</li>
                <li>15 videos saved on your profile (vs fewer for free users)</li>
                <li>Access to premium customizations</li>
                <li>50% reduced forge time</li>
                <li>6 Freeze tokens max (vs 3 for free users)</li>
                <li>Premium badge visible on your profile and in rankings</li>
              </ul>
              <p>
                Available as <strong>Monthly</strong>, <strong>Annual</strong>, or <strong>Founder</strong> (lifetime) subscription.
                The Founder plan additionally includes an exclusive lifetime badge, VIP community access,
                influence on future updates, and periodic exclusive gifts.
              </p>
            </div>

            <div className="faq-item">
              <h3>🔄 How do I restore my purchases?</h3>
              <p>
                If your Premium subscription is not showing after reinstalling or switching devices,
                make sure you are signed in with the same account used for the original purchase, then manage your subscription directly through your platform:
              </p>
              <ul>
                <li><strong>iOS:</strong> Settings → [Your Name] → Subscriptions → Flow XP</li>
                <li><strong>Android:</strong> Google Play → Profile → Payments &amp; subscriptions → Subscriptions → Flow XP</li>
              </ul>
              <p>If the issue persists, contact us at <a href="mailto:flowledgeteam@gmail.com">flowledgeteam@gmail.com</a></p>
            </div>

            <div className="faq-item">
              <h3>🔕 How do I manage notifications?</h3>
              <p>
                Notifications are managed through your device settings, not inside the app:
              </p>
              <ul>
                <li><strong>iOS:</strong> Settings → Flow XP → Notifications</li>
                <li><strong>Android:</strong> Settings → Apps → Flow XP → Notifications</li>
              </ul>
            </div>

            <div className="faq-item">
              <h3>🗑️ How do I delete my account?</h3>
              <p>
                To permanently delete your account:
              </p>
              <ol>
                <li>Open the Flow XP app</li>
                <li>Go to Settings</li>
                <li>Tap "Delete My Account"</li>
                <li>Confirm deletion</li>
              </ol>
              <p>
                ⚠️ This action is permanent. All your data (progress, videos, messages, items) will be deleted within 30 days.
              </p>
              <p><a href="/data-deletion">Learn more about data deletion</a></p>
            </div>

            <div className="faq-item">
              <h3>🐛 I found a bug or issue</h3>
              <p>
                You can report bugs in two ways:
              </p>
              <ul>
                <li>
                  <strong>Discord community:</strong>{' '}
                  <a href="https://discord.gg/cmUNHB5Tyu" target="_blank" rel="noopener noreferrer">
                    Join our Discord
                  </a>{' '}
                  and post in the bugs channel — the community and team will see it quickly
                </li>
                <li>
                  <strong>Email:</strong>{' '}
                  <a href="mailto:flowledgeteam@gmail.com">flowledgeteam@gmail.com</a>
                </li>
              </ul>
              <p>Please include:</p>
              <ul>
                <li>Description of the issue</li>
                <li>Steps to reproduce it</li>
                <li>Your device model and OS version (iOS or Android)</li>
                <li>Screenshots or screen recordings if possible</li>
              </ul>
            </div>

          </div>
        </section>

        <section className="additional-help">
          <div className="help-card">
            <h2>Still Need Help?</h2>
            <p>Can't find what you're looking for? Contact our support team directly.</p>
            <a href="mailto:flowledgeteam@gmail.com" className="btn btn-primary">
              Email Support
            </a>
          </div>
        </section>
      </div>
    </div>
  );
};

export default Support;
